import { useCallback, useEffect, useRef, useState } from 'react';
import WebSocketService from '@/services/websocket';

/**
 * WebSocket 连接实例信息
 */
interface WebSocketConnection {
  service: WebSocketService;
  subscribers: Set<string>; // 订阅者 ID 集合
  latestMessage: API.RobotMqttResponse | null;
}

/**
 * 消息订阅者
 */
interface MessageSubscriber {
  id: string;
  channel: string;
  handler: (message: API.RobotMqttResponse) => void;
}

const DEFAULT_CHANNEL = 'tourism';

/**
 * 全局机器人 WebSocket 管理器
 */
export default function useWebSocketModel() {
  // 存储所有 WebSocket 连接，key 为机器人序列号
  const connectionsRef = useRef<Map<string, WebSocketConnection>>(new Map());
  // 消息订阅者
  const subscribersRef = useRef<Map<string, MessageSubscriber>>(new Map());
  // 强制更新组件的状态
  const [, forceUpdate] = useState({});

  /**
   * 连接到指定频道的 WebSocket
   */
  const connect = useCallback((channel: string = DEFAULT_CHANNEL) => {
    if (!channel) {
      return null;
    }

    // 如果已存在连接，直接返回
    if (connectionsRef.current.has(channel)) {
      const connection = connectionsRef.current.get(channel)!;
      console.log(
        `[WebSocket Manager] Reusing connection: %c${channel}`,
        'color: green',
      );
      return connection.service;
    }

    // 创建新的 WebSocket 连接
    console.log(
      `[WebSocket Manager] Creating connection: %c${channel}`,
      'color: green',
    );
    const service = new WebSocketService({
      url: `/mqtt/robot-${channel}`,
      debug: true,
      onOpen: () => {
        console.log(
          `[WebSocket Manager] Connected to %c${channel}`,
          'color: green',
        );
        forceUpdate({});
      },
      onClose: () => {
        console.log(
          `[WebSocket Manager] Closed connection to %c${channel}`,
          'color: green',
        );
        forceUpdate({});
      },
      onError: (error) => {
        console.error(
          `[WebSocket Manager] Error: %c${channel}`,
          'color: green',
          error,
        );
        forceUpdate({});
      },
      onMessage: (message: API.RobotMqttResponse) => {
        // 更新最新消息
        const connection = connectionsRef.current.get(channel);
        if (connection) {
          connection.latestMessage = message;

          // 通知所有订阅者
          subscribersRef.current.forEach((subscriber) => {
            if (subscriber.channel === channel) {
              try {
                subscriber.handler(message);
              } catch (error) {
                console.error(
                  `[WebSocket Manager] Message handling error: %c${channel}`,
                  'color: green',
                  error,
                );
              }
            }
          });

          forceUpdate({});
        }
      },
    });

    // 连接
    service.connect();

    // 存储连接
    connectionsRef.current.set(channel, {
      service,
      subscribers: new Set(),
      latestMessage: null,
    });

    return service;
  }, []);

  /**
   * 断开指定频道的 WebSocket 连接
   */
  const disconnect = useCallback((channel: string = DEFAULT_CHANNEL) => {
    const connection = connectionsRef.current.get(channel);
    if (connection) {
      console.log(
        `[WebSocket Manager] Disconnecting: %c${channel}`,
        'color: green',
      );
      connection.service.close();
      connectionsRef.current.delete(channel);

      // 清理相关订阅者
      subscribersRef.current.forEach((subscriber, id) => {
        if (subscriber.channel === channel) {
          subscribersRef.current.delete(id);
        }
      });

      forceUpdate({});
    }
  }, []);

  /**
   * 获取指定频道的 WebSocket 连接
   */
  const getConnection = useCallback((channel: string = DEFAULT_CHANNEL) => {
    return connectionsRef.current.get(channel);
  }, []);

  /**
   * 发送消息到指定频道
   */
  const sendMessage = useCallback(
    (
      data: API.RobotMqttMessage | API.TASK.TaskControlPayload,
      channel: string = DEFAULT_CHANNEL,
    ) => {
      const connection = connectionsRef.current.get(channel);
      if (connection && connection.service.isConnected()) {
        return connection.service.send(data as API.RobotMqttMessage);
      }
      console.warn(
        `[WebSocket Manager] Not connected: %c${channel}`,
        'color: green',
      );
      return false;
    },
    [],
  );

  /**
   * 订阅指定频道的消息
   */
  const subscribe = useCallback(
    (
      handler: (message: API.RobotMqttResponse) => void,
      channel: string = DEFAULT_CHANNEL,
    ): string => {
      const subscriberId = `${channel}-${Date.now()}-${Math.random()}`;

      subscribersRef.current.set(subscriberId, {
        id: subscriberId,
        channel,
        handler,
      });

      // 确保 WebSocket 已连接
      const connection = connectionsRef.current.get(channel);
      if (connection) {
        connection.subscribers.add(subscriberId);
      }

      console.log(
        `[WebSocket Manager] New subscriber: %c${subscriberId}`,
        'color: green',
      );

      return subscriberId;
    },
    [],
  );

  /**
   * 取消订阅
   */
  const unsubscribe = useCallback((subscriberId: string) => {
    const subscriber = subscribersRef.current.get(subscriberId);
    if (subscriber) {
      const connection = connectionsRef.current.get(subscriber.channel);
      if (connection) {
        connection.subscribers.delete(subscriberId);

        // 如果没有订阅者了，可以选择断开连接（可选）
        // if (connection.subscribers.size === 0) {
        //   disconnect(subscriber.robotSn);
        // }
      }

      subscribersRef.current.delete(subscriberId);
      console.log(
        `[WebSocket Manager] Canceled subscriber: %c${subscriberId}`,
        'color: green',
      );
    }
  }, []);

  /**
   * 获取所有活跃连接
   */
  const getActiveConnections = useCallback(() => {
    const connections: {
      channel: string;
      isConnected: boolean;
      subscribers: number;
    }[] = [];
    connectionsRef.current.forEach((connection, channel) => {
      connections.push({
        channel,
        isConnected: connection.service.isConnected(),
        subscribers: connection.subscribers.size,
      });
    });
    return connections;
  }, []);

  /**
   * 清理所有连接（用于登出等场景）
   */
  const disconnectAll = useCallback(() => {
    console.log('[WebSocket Manager] Disconnecting all connections');
    connectionsRef.current.forEach((connection) => {
      connection.service.close();
    });
    connectionsRef.current.clear();
    subscribersRef.current.clear();
    forceUpdate({});
  }, []);

  return {
    connect,
    disconnect,
    getConnection,
    sendMessage,
    subscribe,
    unsubscribe,
    getActiveConnections,
    disconnectAll,
  };
}
